import { useMemo } from "react";
import { CommentService } from "@/services/CommentsService";
import { useQuery } from "@tanstack/react-query";
import type { Comment, CommentFilters, CommentSort } from "@/types/Comment";
import type { PaginatedResponse } from "@/types/Pagination";

interface UseCommentsQueryOptions {
  filters?: CommentFilters;
  sort?: CommentSort;
  limit?: number;
  skip?: number;
  enabled?: boolean;
  staleTime?: number;
}

export function useCommentsQuery(options: UseCommentsQueryOptions = {}) {
  const {
    filters,
    sort = { field: "createdAt", order: "desc" },
    limit,
    skip,
    enabled = true,
    staleTime = 5 * 60 * 1000, // 5 minutes
  } = options;

  const commentService = useMemo(() => new CommentService(), []);

  return useQuery<PaginatedResponse<Comment>>({
    queryKey: ["comments", filters, sort, limit, skip],
    queryFn: () => commentService.getComments(filters, sort, limit, skip),
    enabled,
    staleTime,
    refetchOnWindowFocus: false,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff up to 30 seconds
    select: (data) => data, // Return full paginated response
  });
}
